import PageLayout from "../../layouts/PageLayout.tsx";
import {Card, SubCard} from "../../components/card/Card.tsx";
import {z} from "zod";
import {getAddress, isAddress} from "viem";
import {zodResolver} from "@hookform/resolvers/zod";
import {useForm} from "react-hook-form";
import {useState} from "react";
import {ErrorMessage} from "@hookform/error-message";
import {useQuery} from "@tanstack/react-query";
import {getWhitelistedTokens, type Token,} from "../../repository/TokenWhitelistRepository.ts";
import {useWriteEscrowFactoryCreateEscrow} from "../../generated.ts";
import toast from "react-hot-toast";
import {Recap} from "../../components/Recap.tsx";
import {useNavigate} from "react-router";
import {useAccount, usePublicClient} from "wagmi";
import {StaffGuard} from "../../guards/StaffGuard.tsx";
import {StaffType} from "../../hooks/useStaffGuard.ts";

const makeAddressSchema = (message: string) =>
  z
    .string()
    .min(1, "Address is required")
    .refine((val) => isAddress(val) && getAddress(val), {
      message,
    });

const tokenSchema = z.object({
  address: z.string().min(1, "Please select a token."),
  symbol: z.string().min(1, "Invalid symbol."),
  decimals: z.number().min(0, "Invalid decimals."),
});

const stringifiedTokenToObject = z
  .string()
  .min(1, "Please select a token.")
  .transform((s, ctx) => {
    try {
      const parsed = JSON.parse(s);
      return tokenSchema.parse(parsed);
    } catch {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Invalid token.",
      });
      return z.NEVER;
    }
  });

const escrowSchema = z
  .object({
    // Seller
    sellerAddress: makeAddressSchema("Invalid seller address."),
    sellerTradeAmount: z.coerce
      .number({ invalid_type_error: "Please enter a number." })
      .min(1, "Please enter an amount."),
    sellerTradeToken: stringifiedTokenToObject,
    sellerDepositAmount: z.coerce
      .number({ invalid_type_error: "Please enter a number." })
      .min(1, "Please enter the seller’s penalty deposit amount."),
    sellerDepositToken: stringifiedTokenToObject,
    sellerFeesAmount: z.coerce
      .number({ invalid_type_error: "Please enter a number." })
      .min(0, "Please enter the fees amount."),
    sellerFeesToken: stringifiedTokenToObject,

    // Buyer
    buyerAddress: makeAddressSchema("Invalid buyer address."),
    buyerTradeAmount: z.coerce
      .number({ invalid_type_error: "Please enter a number." })
      .min(1, "Please enter an amount."),
    buyerTradeToken: stringifiedTokenToObject,
    buyerDepositAmount: z.coerce
      .number({ invalid_type_error: "Please enter a number." })
      .min(1, "Please enter the buyer’s penalty deposit amount."),
    buyerDepositToken: stringifiedTokenToObject,
    buyerFeesAmount: z.coerce
      .number({ invalid_type_error: "Please enter a number." })
      .min(0, "Please enter the fees amount."),
    buyerFeesToken: stringifiedTokenToObject,

    // Conditions
    duration: z.coerce
      .number({ invalid_type_error: "Please enter a number." })
      .min(1, "Please specify the contract duration."),
  })
  .refine((data) => data.sellerAddress !== data.buyerAddress, {
    message: "Seller and Buyer addresses must be different.",
    path: ["buyerAddress"],
  });

export type EscrowFormValues = z.infer<typeof escrowSchema>;

const CreateEscrow = () => {
  const { data: whitelistedTokens } = useQuery({
    queryKey: ["whitelisted-tokens"],
    queryFn: () => getWhitelistedTokens(),
    initialData: [],
  });

  return (
    <PageLayout
      breadcrumbs={[
        { name: "Factory", href: "/factory" },
        { name: "Create a new escrow contract", href: "" },
      ]}
    >
        <StaffGuard staffTypes={[StaffType.OWNER]}>
            <Card title={"Create a new escrow contract"}>
                <EscrowBuyerSellerForm tokens={whitelistedTokens} />
            </Card>
        </StaffGuard>

    </PageLayout>
  );
};

const EscrowBuyerSellerForm = ({ tokens }: { tokens: Token[] }) => {
  const [formData, setFormData] = useState<EscrowFormValues | null>(null);
  const { address } = useAccount();
  const publicClient = usePublicClient();
  const [isAddPending, setIsAddPending] = useState(false);
  const { writeContractAsync: createEscrow } =
    useWriteEscrowFactoryCreateEscrow();
  const navigate = useNavigate();

  const handleCreateEscrow = async (data: EscrowFormValues) => {
    if (!address) return;
    if (!publicClient) return;

    setIsAddPending(true);

    let txid;
    try {
      txid = await createEscrow({
        args: [
          {
            account: getAddress(data.sellerAddress),
            exchangeToken: getAddress(data.sellerTradeToken.address),
            exchangeAmount: BigInt(
              Number(data.sellerTradeAmount) *
                10 ** data.sellerTradeToken.decimals,
            ),
            penaltyToken: getAddress(data.sellerDepositToken.address),
            penaltyAmount: BigInt(
              Number(data.sellerDepositAmount) *
                10 ** data.sellerDepositToken.decimals,
            ),
            feeToken: getAddress(data.sellerFeesToken.address),
            feeAmount: BigInt(
              Number(data.sellerFeesAmount) *
                10 ** data.sellerFeesToken.decimals,
            ),
          },
          {
            account: getAddress(data.buyerAddress),
            exchangeToken: getAddress(data.buyerTradeToken.address),
            exchangeAmount: BigInt(
              Number(data.buyerTradeAmount) *
                10 ** data.buyerTradeToken.decimals,
            ),
            penaltyToken: getAddress(data.buyerDepositToken.address),
            penaltyAmount: BigInt(
              Number(data.buyerDepositAmount) *
                10 ** data.buyerDepositToken.decimals,
            ),
            feeToken: getAddress(data.buyerFeesToken.address),
            feeAmount: BigInt(
              Number(data.buyerFeesAmount) * 10 ** data.buyerFeesToken.decimals,
            ),
          },
          BigInt(data.duration * 3600),
        ],
      });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      toast.error("Failed to create escrow contract.");
      setIsAddPending(false);
      return;
    }

    await toast
      .promise(publicClient.waitForTransactionReceipt({ hash: txid }), {
        loading: "Waiting for transaction confirmation...",
        success: "Escrow contract created successfully!",
        error: "Failed to create escrow contract.",
      })
      .then(() => {
        setIsAddPending(false);
        navigate("/factory");
        setFormData(null);
      });
  };

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<EscrowFormValues>({
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    resolver: zodResolver(escrowSchema),
  });

  const handleGoBack = () => {
    setFormData(null);
  };

  if (formData) {
    return (
      <Recap
        data={formData}
        onCreate={() => handleCreateEscrow(formData)}
        onGoBack={handleGoBack}
        isAddPending={isAddPending}
      />
    );
  }

  return (
    <form
      onSubmit={handleSubmit((data) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        setFormData(data);
      })}
    >
      <div className="flex flex-col gap-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <SubCard title="Seller">
            <fieldset className="fieldset space-y-3">
              <legend />

              {/* Seller address */}
              <div className={"flex flex-col gap-2"}>
                <input
                  {...register("sellerAddress")}
                  type="text"
                  className="input input-bordered"
                  placeholder="Seller wallet address"
                />
                <ErrorMessage
                  errors={errors}
                  name="sellerAddress"
                  render={({ message }) => (
                    <p className="text-error text-sm">{message}</p>
                  )}
                />
                <p className="label">Seller address</p>
              </div>

              {/* Seller trade */}
              <div className={"flex flex-col gap-2"}>
                <legend className="fieldset-legend text-base font-medium mt-4">
                  Exchange amount
                </legend>
                <div className="join w-1/2">
                  <input
                    {...register("sellerTradeAmount", { valueAsNumber: true })}
                    type="number"
                    className="input input-bordered join-item"
                    placeholder="Exchange amount"
                  />
                  <select
                    {...register("sellerTradeToken")}
                    className="select select-bordered join-item w-auto"
                  >
                    <option value="">Token</option>
                    {tokens.map((token) => (
                      <option key={token.address} value={JSON.stringify(token)}>
                        {token.symbol}
                      </option>
                    ))}
                  </select>
                </div>
                <ErrorMessage
                  errors={errors}
                  name="sellerTradeAmount"
                  render={({ message }) => (
                    <p className="text-error text-sm">{message}</p>
                  )}
                />
                <ErrorMessage
                  errors={errors}
                  name="sellerTradeToken"
                  render={({ message }) => (
                    <p className="text-error text-sm">{message}</p>
                  )}
                />
                <p className="label">
                  Amount and token the seller wants to trade.
                </p>
              </div>

              {/* Seller deposit */}
              <div className={"flex flex-col gap-2"}>
                <legend className="fieldset-legend text-base font-medium mt-4">
                  Penalty deposit
                </legend>

                <div className="join w-1/2">
                  <input
                    {...register("sellerDepositAmount", {
                      valueAsNumber: true,
                    })}
                    type="number"
                    className="input input-bordered join-item"
                    placeholder="Penalty deposit amount"
                  />
                  <select
                    {...register("sellerDepositToken")}
                    className="select select-bordered join-item w-auto"
                  >
                    <option value="">Token</option>
                    {tokens.map((token) => (
                      <option key={token.address} value={JSON.stringify(token)}>
                        {token.symbol}
                      </option>
                    ))}
                  </select>
                </div>
                <ErrorMessage
                  errors={errors}
                  name="sellerDepositAmount"
                  render={({ message }) => (
                    <p className="text-error text-sm">{message}</p>
                  )}
                />
                <ErrorMessage
                  errors={errors}
                  name="sellerDepositToken"
                  render={({ message }) => (
                    <p className="text-error text-sm">{message}</p>
                  )}
                />
                <p className="label">
                  Deposit locked as a guarantee. Forfeited if the contract
                  expires.
                </p>
              </div>

              {/* Seller fees */}
              <div className={"flex flex-col gap-2"}>
                <legend className="fieldset-legend text-base font-medium mt-4">
                  Fees
                </legend>

                <div className="join w-1/2">
                  <input
                    {...register("sellerFeesAmount", {
                      valueAsNumber: true,
                    })}
                    type="number"
                    className="input input-bordered join-item"
                    placeholder="Fees amount"
                  />
                  <select
                    {...register("sellerFeesToken")}
                    className="select select-bordered join-item w-auto"
                  >
                    <option value="">Token</option>
                    {tokens.map((token) => (
                      <option key={token.address} value={JSON.stringify(token)}>
                        {token.symbol}
                      </option>
                    ))}
                  </select>
                </div>
                <ErrorMessage
                  errors={errors}
                  name="sellerFeesAmount"
                  render={({ message }) => (
                    <p className="text-error text-sm">{message}</p>
                  )}
                />
                <ErrorMessage
                  errors={errors}
                  name="sellerFeesToken"
                  render={({ message }) => (
                    <p className="text-error text-sm">{message}</p>
                  )}
                />
                <p className="label">Applicable seller fees.</p>
              </div>
            </fieldset>
          </SubCard>

          {/* Buyer */}
          <SubCard title="Buyer">
            <fieldset className="fieldset space-y-3">
              <legend />

              {/* Buyer address */}
              <div className={"flex flex-col gap-2"}>
                <input
                  {...register("buyerAddress")}
                  type="text"
                  className="input input-bordered"
                  placeholder="Buyer wallet address"
                />
                <ErrorMessage
                  errors={errors}
                  name="buyerAddress"
                  render={({ message }) => (
                    <p className="text-error text-sm">{message}</p>
                  )}
                />
                <p className="label">Buyer address</p>
              </div>

              {/* Buyer trade */}
              <div className={"flex flex-col gap-2"}>
                <legend className="fieldset-legend text-base font-medium mt-4">
                  Exchange amount
                </legend>
                <div className="join w-1/2">
                  <input
                    {...register("buyerTradeAmount", { valueAsNumber: true })}
                    type="number"
                    className="input input-bordered join-item"
                    placeholder="Exchange amount"
                  />
                  <select
                    {...register("buyerTradeToken")}
                    className="select select-bordered join-item w-auto"
                  >
                    <option value="">Token</option>
                    {tokens.map((token) => (
                      <option key={token.address} value={JSON.stringify(token)}>
                        {token.symbol}
                      </option>
                    ))}
                  </select>
                </div>
                <ErrorMessage
                  errors={errors}
                  name="buyerTradeAmount"
                  render={({ message }) => (
                    <p className="text-error text-sm">{message}</p>
                  )}
                />
                <ErrorMessage
                  errors={errors}
                  name="buyerTradeToken"
                  render={({ message }) => (
                    <p className="text-error text-sm">{message}</p>
                  )}
                />
                <p className="label">
                  Amount and token the buyer wants to trade.
                </p>
              </div>

              {/* Buyer deposit */}
              <div className={"flex flex-col gap-2"}>
                <legend className="fieldset-legend text-base font-medium mt-4">
                  Penalty deposit
                </legend>

                <div className="join w-1/2">
                  <input
                    {...register("buyerDepositAmount", { valueAsNumber: true })}
                    type="number"
                    className="input input-bordered join-item"
                    placeholder="Penalty deposit amount"
                  />
                  <select
                    {...register("buyerDepositToken")}
                    className="select select-bordered join-item w-auto"
                  >
                    <option value="">Token</option>
                    {tokens.map((token) => (
                      <option key={token.address} value={JSON.stringify(token)}>
                        {token.symbol}
                      </option>
                    ))}
                  </select>
                </div>
                <ErrorMessage
                  errors={errors}
                  name="buyerDepositAmount"
                  render={({ message }) => (
                    <p className="text-error text-sm">{message}</p>
                  )}
                />
                <ErrorMessage
                  errors={errors}
                  name="buyerDepositToken"
                  render={({ message }) => (
                    <p className="text-error text-sm">{message}</p>
                  )}
                />
                <p className="label">
                  Deposit locked as a guarantee. Forfeited if the contract
                  expires.
                </p>
              </div>

              {/* Buyer fees */}
              <div className={"flex flex-col gap-2"}>
                <legend className="fieldset-legend text-base font-medium mt-4">
                  Fees
                </legend>

                <div className="join w-1/2">
                  <input
                    {...register("buyerFeesAmount", {
                      valueAsNumber: true,
                    })}
                    type="number"
                    className="input input-bordered join-item"
                    placeholder="Fees amount"
                  />
                  <select
                    {...register("buyerFeesToken")}
                    className="select select-bordered join-item w-auto"
                  >
                    <option value="">Token</option>
                    {tokens.map((token) => (
                      <option key={token.address} value={JSON.stringify(token)}>
                        {token.symbol}
                      </option>
                    ))}
                  </select>
                </div>
                <ErrorMessage
                  errors={errors}
                  name="buyerFeesAmount"
                  render={({ message }) => (
                    <p className="text-error text-sm">{message}</p>
                  )}
                />
                <ErrorMessage
                  errors={errors}
                  name="buyerFeesToken"
                  render={({ message }) => (
                    <p className="text-error text-sm">{message}</p>
                  )}
                />
                <p className="label">Applicable buyer fees.</p>
              </div>
            </fieldset>
          </SubCard>
        </div>

        {/* Contract conditions */}
        <div>
          <SubCard title="Contract conditions">
            <fieldset className="fieldset mt-4 space-y-2">
              <div className={"flex flex-col gap-2"}>
                <div className={"join w-1/2"}>
                  <input
                    {...register("duration", { valueAsNumber: true })}
                    type="number"
                    className="input input-bordered"
                    placeholder="Contract duration (in hours)"
                  />
                  {timeOptions.map((option) => (
                    <button
                      key={option.value}
                      type={"button"}
                      className={"btn"}
                      onClick={() => setValue("duration", option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <ErrorMessage
                  errors={errors}
                  name="duration"
                  render={({ message }) => (
                    <p className="text-error text-sm">{message}</p>
                  )}
                />
                <p className="label">
                  Maximum duration before automatic escrow expiration.
                </p>
              </div>
            </fieldset>

            <fieldset className="fieldset mt-4 space-y-2">
              <div>
                <button type="submit" className="btn btn-primary">
                  Preview contract
                </button>
              </div>
            </fieldset>
          </SubCard>
        </div>
      </div>
    </form>
  );
};

const timeOptions: TimeOption[] = [
  { label: "1 hour", value: 1 },
  { label: "24 hours", value: 24 },
  { label: "1 week", value: 24 * 7 },
  { label: "1 month", value: 24 * 30 },
];

type TimeOption = {
  label: string;
  value: number; // in hours
};

export default CreateEscrow;
